import { IControlProperties } from '../../../../../../entities';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { BCExtremeGridEntities } from '@bimser/components';
import { IDictionary, IItem, MLHelper, isNullOrUndefined } from '@bimser/core';
import { IDataGridColumnCollection, IControlDataGridColumn } from '../../entities';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { IEditingStartEventArgs } from '../../../../entities/EventArgs';
import GetIconByName from '@bimser/icons/lib/getIcon16ByName';
import _isEmpty from 'lodash/isEmpty';
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

export default function convertFieldsForMobile(
	fields: IDataGridColumnCollection,
	values?: any,
	columns?: BCExtremeGridEntities.IExtremeGridColumn[],
	gridName?: string,
	editing: boolean = false,
	onEditingRow?: IEditingStartEventArgs
) {
	return fields
		.map(field => {
			if (field.isPrimaryKey && field.editControl) {
				field.editControl.readOnly = editing;
			}

			switch (field.editType) {
				case 'textBox':
					return textbox(field, values);
				case 'checkBox':
					return checkbox(field, values, editing);
				case 'datePicker':
					return dateTimePicker(field, values);
				case 'numberBox':
					return numberbox(field, values);
				case 'dateTimePicker':
					return dateTimePicker(field, values);
				case 'timePicker':
					return timePicker(field, values);
				case 'comboBox':
					return comboBox(field, values, columns, onEditingRow);
				case 'multiLanguageTextBox':
					return textbox(field, values);
				case 'dateRangePicker':
					return dateRangePicker(field, values);
				case 'fileSelect':
					return fileSelect(field, values);
				default:
					break;
			}
		})
		.filter(x => x)
		.map(x => ({ ...x, gridName: gridName || '' }));
}

// #region Controls
const textbox = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const value = values?.find((x: any) => x.name === field.name)?.value;

	return {
		...multiLanguageTextControl(field, value),
		value: field.editType === 'multiLanguageTextBox' ? MLHelper.getMLText(value) : value,
		readOnly:
			control.readOnly || field.autoIncrement || !field.editingEnabled || !control.enabled,
		disabled: !control.enabled || !control.clientEnabled
	};
};

const numberbox = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const value = values?.find((x: any) => x.name === field.name)?.value;

	return {
		...editControl(field),
		max: control.max,
		min: control.min,
		step: control.step,
		useThousandSeparator: field.useThousandSeparator,
		precision: field.precision,
		sizeType: control.sizeType,
		disabled: !control.enabled || !control.clientEnabled,
		value: value || null,
		text: value?.toString() || ''
	};
};

const fileSelect = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const value = values?.find((x: any) => x.name === field.name)?.value;
	return {
		...editControl(field),
		files: typeof value == 'string' ? null : value,
		allowSelectMultiple: control.allowSelectMultiple,
		uploadTargetPath: false,
		value: typeof value === 'string' ? value.split(',')[1] ?? value : null,
		allowRemove: control.allowRemove
	};
};

const checkbox = (
	field: IControlDataGridColumn<IControlProperties>,
	values: any,
	editing: boolean
) => {
	const control: any = field.editControl;

	let value: boolean = values?.find((x: any) => x.name === field.name)?.value;

	if (typeof value !== 'boolean' && !editing && !isNullOrUndefined(field.defaultValue)) {
		value = field.defaultValue;
		if (values?.length) {
			values.forEach((x: any) => {
				if (x.name === field.name) {
					x.value = value;
					x.rowValue = value;
				}
			});
		} else if (values) {
			values.push({
				name: field.name,
				value: value,
				rowValue: value
			});
		}
	}

	return {
		...labeledInputBase(field),
		disabled: !control.enabled || !control.clientEnabled,
		value: value || false
	};
};

const dateTimePicker = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const format = field.format || (field.editType === 'datePicker' ? 'L' : 'L LT');
	const value = values?.find((x: any) => x.name === field.name)?.value;

	return {
		...editControl(field),
		format: format,
		showTime: control.showTime,
		showToday: control.showToday,
		disabled: !control.enabled || !control.clientEnabled,
		value: value ? (dayjs(value).format(format) as any) : null,
		style: { width: '100%', ...control.style },
		allowClear: control.allowClear,
		setTodayAsDefault: control.setTodayAsDefault,
		sizeType: control.sizeType
	};
};

const timePicker = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const format = field.format || dayjs().localeData().longDateFormat('LTS');
	const value = values?.find((x: any) => x.name === field.name)?.value;

	return {
		...editControl(field),
		use12Hours: control.use12Hours,
		hourStep: control.hourStep,
		minuteStep: control.minuteStep,
		secondStep: control.secondStep,
		disabledHours: control.disabledHours.map((i: any) => Number(i)),
		format: format,
		disabled: !control.enabled || !control.clientEnabled,
		value: value ? (dayjs(value).format(format) as any) : null, //dayjs(dayjs.utc(this.value).toDate())
		style: { width: '100%', ...control.style },
		size: control.sizeType,
		allowClear: control.allowClear
	};
};

const dateRangePicker = (field: IControlDataGridColumn<IControlProperties>, values: any) => {
	const control: any = field.editControl;
	const format = field.format || dayjs().localeData().longDateFormat('L');
	const value = values?.find((x: any) => x.name === field.name)?.value || [];

	return {
		...editControl(field),
		format: control.format,
		showTime: control.showTime,
		showToday: control.showToday,
		style: { width: '100%', ...control.style },
		disabled: !control.enabled || !control.clientEnabled,
		value: [
			value[0] ? dayjs(new Date(value[0])).format(format) : null,
			value[1] ? dayjs(new Date(value[1])).format(format) : null
		],
		allowClear: true,
		disabledTime: control.disabledTime,
		allowEmpty: [control.startDate.allowEmpty, control.endDate.allowEmpty],
		disabledDate: control.disabledDate,
		size: control.sizeType,
		startDate: control.startDate,
		endDate: control.endDate
	};
};

const selectedControl = (rowValue: string, option: any, value: string) => {
	const optionKey = option.key?.toString();
	const optionText = MLHelper.getMLText(option.text).trim();
	const valueText = value?.toString()?.trim();
	if (rowValue) {
		return rowValue === optionKey;
	}
	return optionKey === valueText || optionText === valueText;
};

const comboBox = (
	field: IControlDataGridColumn<IControlProperties>,
	values: any,
	columns?: BCExtremeGridEntities.IExtremeGridColumn[],
	onEditingRow?: IEditingStartEventArgs
) => {
	const control: any = field.editControl;
	const value = values?.find(x => x.name === field.name)?.value || '';
	let options: IItem[] = [];
	let controlOptions: any;
	let rowValue;

	if (_isEmpty(control.dataSource.name)) {
		controlOptions = [...control.items];
	} else if (columns?.length) {
		controlOptions = columns.find(c => c?.name === field.name)?.editorOptions?.options;
	}

	if (onEditingRow?.row) {
		const editingSelectValue = onEditingRow.row[field.name];
		rowValue =
			typeof editingSelectValue == 'string' ? editingSelectValue : editingSelectValue?.key;
		const isIncludes = controlOptions?.find(item => item.key == rowValue);
		if (!isIncludes) {
			controlOptions.push({ ...onEditingRow.row[field.name] });
		}
	}

	options = controlOptions
		?.filter(item => !isNullOrUndefined(item.key))
		.map(i => {
			const item: IItem = {
				key: i.key,
				value: i.value,
				selected: selectedControl(rowValue?.toString(), i, value?.toString()),
				text: MLHelper.getMLText(i.text)
			};

			if (i.icon) {
				item.icon = GetIconByName(i.icon).info;
			}

			return item;
		});

	return {
		...comboBoxBase(field),
		showSearch: !control.readOnly,
		isStaticSearch: !(control.dataSource?.name && !control.dataSource.runAtServer),
		options,
		disabled: control.loading || !control.enabled || !control.clientEnabled,
		className: control.style.backgroundColor || '',
		size: control.sizeType,
		style: { ...{ width: '100%' }, ...({ '--bgColor': control.style.backgroundColor } as any) }
	};
};

// #endregion

// #region Base Classes

const multiLanguageTextControl = (
	field: IControlDataGridColumn<IControlProperties>,
	values: IDictionary<string>
) => {
	const enableMultiLanguageText = field.editType === 'multiLanguageTextBox';

	return {
		...textControl(field),
		enableMultiLanguageText,
		multiLanguageText: values || {}
	};
};

const labeledInputBase = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...inputControl(field),
		label: MLHelper.getMLText(control.label)
	};
};

const comboBoxBase = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;

	return {
		...listControl(field),
		allowClear: control.allowClear,
		placeholder: control.placeholder
	};
};

const listControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;

	return {
		...editControl(field),
		data: control.data,
		dataSource: control.dataSource,
		valueType: control.valueType,
		dataSourceType: control.dataSourceType
	};
};

const textControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...editControl(field),
		maxLength: control.maxLength
	};
};

const editControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...editControlBase(field),
		placeholder: control.placeholder,
		textAlign: control.textAlign
	};
};

const editControlBase = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...inputControl(field),
		text: control.text || ''
	};
};

const inputControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...dataBoundControl(field),
		value: control.value,
		tabIndex: control.tabIndex
	};
};

const dataBoundControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...visualControl(field),
		readOnly:
			control.readOnly || field.autoIncrement || !field.editingEnabled || !control.enabled
	};
};

const visualControl = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		...controlBase(field),
		caption: {
			...control.caption,
			text: MLHelper.getMLText(field.caption)
		},
		title: control.title || {},
		loading: control.loading,
		visible: control.visible && field.visible,
		clientVisible: control.clientVisible,
		enabled: control.enabled,
		clientEnabled: control.clientEnabled,
		style: control.style,
		containerStyle: control.containerStyle,
		customClassName: control.customClassName
	};
};

const controlBase = (field: IControlDataGridColumn<IControlProperties>) => {
	const control: any = field.editControl;
	return {
		name: field.name,
		controlId: control.controlId,
		controlType: control.controlType
	};
};

// #endregion
